import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, UserPlus, Calendar as Cal, CalendarCheck, Megaphone, ArrowRight, Tag } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";

const KPI = ({ icon: I, label, value, color = "wine" }) => (
  <div className="rounded-2xl border border-neutral-100 bg-white p-5">
    <div className={`grid h-10 w-10 place-items-center rounded-xl bg-${color}-50 text-${color}-600`}><I className="h-5 w-5" /></div>
    <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">{value}</div>
    <div className="text-xs font-medium text-neutral-600">{label}</div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [poleMap, setPoleMap] = useState({});

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/poles").then((r) => {
      const map = {};
      (r.data || []).forEach((p) => { map[p.id] = p.name; });
      setPoleMap(map);
    }).catch(() => {});
  }, []);

  const poleLabel = (id) => poleMap[id] || id || "—";

  if (!stats) return <PortalLayout sidebar="admin"><div className="py-20 text-center text-neutral-500">Chargement...</div></PortalLayout>;

  const totalPole = (stats.pole_breakdown || []).reduce((s, p) => s + p.count, 0);
  const totalCat = (stats.category_breakdown || []).reduce((s, c) => s + c.count, 0);

  return (
    <PortalLayout sidebar="admin">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy md:text-4xl">Tableau de bord</h1>
        <p className="mt-1 text-neutral-600">Bienvenue {user?.first_name}, voici l&apos;activité de la communauté WineTech.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KPI icon={Users} label="Membres actifs" value={stats.kpis.members} />
        <KPI icon={UserPlus} label="Nouvelles candidatures" value={stats.kpis.new_applications} />
        <KPI icon={Cal} label="Événements à venir" value={stats.kpis.upcoming_events} />
        <KPI icon={CalendarCheck} label="Rendez-vous planifiés" value={stats.kpis.meetings} />
        <KPI icon={Megaphone} label="Actualités publiées" value={stats.kpis.news_published} />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <h2 className="font-serif text-xl text-neutral-900">Membres par pôle</h2>
          <p className="mt-0.5 text-xs text-neutral-500">{stats.pole_breakdown.length} pôle{stats.pole_breakdown.length > 1 ? "s" : ""} sectoriel{stats.pole_breakdown.length > 1 ? "s" : ""} · {totalPole} membre{totalPole > 1 ? "s" : ""}</p>
          <div className="mt-5 space-y-4">
            {stats.pole_breakdown.length === 0 && <p className="text-sm text-neutral-400">Aucun pôle configuré. Configurez les pôles dans les paramètres.</p>}
            {stats.pole_breakdown.map((p) => {
              const pct = totalPole > 0 ? Math.round((p.count / totalPole) * 100) : 0;
              const isEmpty = !p.pole || p.pole === "—";
              const label = p.label || (isEmpty ? "Non renseigné" : p.pole);
              return (
                <div key={p.pole} data-testid={`dashboard-pole-${p.pole}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className={isEmpty ? "italic text-neutral-400" : "text-neutral-700"}>{label}</span>
                    <span className="font-semibold text-wine-700">{pct}% ({p.count})</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-wine-50">
                    <div className={`h-full rounded-full ${isEmpty ? "bg-neutral-300" : "bg-gradient-to-r from-wine-400 to-wine-600"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="dashboard-categories-block">
          <h2 className="font-serif text-xl text-neutral-900"><Tag className="mr-1 inline h-4 w-4 text-wine-500" /> Membres par catégorie</h2>
          <p className="mt-0.5 text-xs text-neutral-500">{(stats.category_breakdown || []).length} catégorie{(stats.category_breakdown || []).length > 1 ? "s" : ""} d&apos;adhésion · {totalCat} membre{totalCat > 1 ? "s" : ""}</p>
          <div className="mt-5 space-y-4">
            {(stats.category_breakdown || []).length === 0 && <p className="text-sm text-neutral-400">Aucune catégorie renseignée pour le moment.</p>}
            {(stats.category_breakdown || []).map((c) => {
              const pct = totalCat > 0 ? Math.round((c.count / totalCat) * 100) : 0;
              return (
                <div key={c.category_id} data-testid={`dashboard-category-${c.category_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-700">{c.category}</span>
                    <span className="font-semibold text-blush-700">{pct}% ({c.count})</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-blush-50">
                    <div className="h-full rounded-full bg-gradient-to-r from-blush-400 to-blush-600" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-neutral-900">Candidatures récentes</h2>
            <Link to="/admin/applications" className="text-xs font-semibold text-wine-600" data-testid="admin-see-applications">Voir toutes <ArrowRight className="ml-1 inline h-3 w-3" /></Link>
          </div>
          <div className="mt-4 space-y-3">
            {stats.recent_applications.slice(0, 5).map((a) => (
              <div key={a.id} className="rounded-xl border border-neutral-100 p-3" data-testid={`admin-app-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif text-sm font-semibold text-neutral-900">{a.company_name}</div>
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{a.status}</span>
                </div>
                <div className="mt-0.5 text-xs text-neutral-500">{a.category} · {a.country}</div>
              </div>
            ))}
            {stats.recent_applications.length === 0 && <p className="text-sm text-neutral-400">Aucune candidature pour le moment.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <h2 className="font-serif text-xl text-neutral-900">Rendez-vous à venir</h2>
          <div className="mt-4 space-y-3">
            {stats.recent_meetings.slice(0, 5).map((m) => (
              <div key={m.id} className="rounded-xl border border-neutral-100 p-3 text-xs" data-testid={`admin-meeting-${m.id}`}>
                <div className="font-semibold text-neutral-800">{m.event_title || m.title || "Rendez-vous"}</div>
                <div className="mt-0.5 text-neutral-500">{m.start ? new Date(m.start).toLocaleString("fr") : (m.created_at ? new Date(m.created_at).toLocaleDateString("fr") : "—")}</div>
              </div>
            ))}
            {stats.recent_meetings.length === 0 && <p className="text-sm text-neutral-400">Aucun rendez-vous planifié.</p>}
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <h2 className="font-serif text-xl text-neutral-900">Top membres</h2>
          <table className="mt-4 w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-500">
              <tr><th className="py-2 text-left">Membre</th><th className="py-2 text-left">Pôle</th><th className="py-2 text-right">Score</th></tr>
            </thead>
            <tbody>
              {stats.top_members.map((m) => (
                <tr key={m.id} className="border-t border-neutral-100" data-testid={`admin-top-${m.id}`}>
                  <td className="py-3"><div className="flex items-center gap-2"><CompanyLogo src={m.logo} name={m.name} size={32} className="rounded-lg" /><div><div className="font-medium text-neutral-900">{m.name}</div><div className="text-xs text-neutral-500">{m.city}</div></div></div></td>
                  <td className="py-3 text-xs">{m.pole && <span className="rounded-full bg-wine-50 px-2 py-0.5 font-semibold text-wine-700">{poleLabel(m.pole)}</span>}</td>
                  <td className="py-3 text-right font-semibold text-wine-700">{m.score || 0} pts</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      <div className="mt-8 rounded-3xl border border-wine-100 bg-gradient-to-br from-wine-50 to-white p-6 md:p-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h3 className="font-serif text-2xl text-neutral-900">Pilotez la croissance de votre communauté</h3>
            <p className="mt-1.5 text-neutral-600">Suivez les indicateurs clés et accompagnez vos membres.</p>
          </div>
          <Link to="/admin/analytics" className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white hover:bg-wine-600" data-testid="admin-view-analytics">
            Voir Analytics <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </PortalLayout>
  );
}
